import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  InputAdornment
} from '@mui/material';
import { Dashboard, Category } from '../services/DashboardsService';
import { LoadingButton } from '@mui/lab';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import TimerIcon from '@mui/icons-material/Timer';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import CodeIcon from '@mui/icons-material/Code';

interface DashboardFormProps {
  initialValues: Dashboard;
  categories: Category[];
  dashboardsByCategory: { [categoryId: string]: Dashboard[] };
  onSave: (dashboard: Dashboard) => Promise<boolean>;
  onCancel: () => void;
  isNew?: boolean;
}

const DashboardForm: React.FC<DashboardFormProps> = ({
  initialValues,
  categories,
  dashboardsByCategory,
  onSave,
  onCancel,
  isNew = false
}) => {
  const [dashboard, setDashboard] = useState<Dashboard>({...initialValues});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle input changes
  const handleChange = (field: keyof Dashboard, value: any) => {
    setDashboard(prev => ({ ...prev, [field]: value }));
    
    // Clear error for the field when changed
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Handle category change
  const handleCategoryChange = (categoryId: number) => {
    const selectedCategory = categories.find(c => c.id === categoryId);
    if (selectedCategory) {
      setDashboard(prev => ({ ...prev, category: selectedCategory }));
    }
  };

  // Handle boolean toggle
  const handleBooleanToggle = (field: keyof Dashboard) => (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(field, event.target.checked);
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!dashboard.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!dashboard.category || !dashboard.category.id) {
      newErrors.category = 'Category is required';
    }
    
    if (!dashboard.type.trim()) {
      newErrors.type = 'Type is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const success = await onSave(dashboard);
      if (success) {
        onCancel(); // Close form on success
      }
    } catch (error) {
      console.error('Error saving dashboard:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const orderMax = !isNew && initialValues.category.id === dashboard.category.id
    ? dashboardsByCategory[initialValues.category.id].length
    : (dashboardsByCategory[dashboard.category.id]?.length || 0) + 1;

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
      
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Name"
            value={dashboard.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            error={!!errors.name}
            helperText={errors.name}
          />
        </Grid>
        
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Short Name"
            value={dashboard.shortName}
            onChange={(e) => handleChange('shortName', e.target.value)}
            error={!!errors.shortName}
            helperText={errors.shortName}
          />
        </Grid>
        
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Subtitle"
            value={dashboard.subtitle}
            onChange={(e) => handleChange('subtitle', e.target.value)}
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth required error={!!errors.category}>
            <InputLabel>Category</InputLabel>
            <Select
              value={dashboard.category?.id || ''}
              label="Category"
              onChange={(e) => handleCategoryChange(e.target.value as number)}
            >
              {categories.map(category => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
            {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
          </FormControl>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth required error={!!errors.type}>
            <InputLabel>Type</InputLabel>
            <Select
              value={dashboard.type}
              label="Type"
              onChange={(e) => handleChange('type', e.target.value)}
            >
              <MenuItem value="superset">Superset</MenuItem>
              <MenuItem value="redirect">Redirect</MenuItem>
            </Select>
            {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Switch 
                checked={dashboard.internalOnly} 
                onChange={handleBooleanToggle('internalOnly')}
              />
            }
            label="Internal Only"
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Switch 
                checked={dashboard.accessOnly} 
                onChange={handleBooleanToggle('accessOnly')}
              />
            }
            label="Access Only"
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Switch
                checked={dashboard.listenReporting}
                onChange={handleBooleanToggle('listenReporting')}
              />
            }
            label="Listen Reporting"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Switch
                checked={dashboard.hxcProgram}
                onChange={handleBooleanToggle('hxcProgram')}
              />
            }
            label="HXC Program"
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Switch 
                checked={dashboard.deactivated} 
                onChange={handleBooleanToggle('deactivated')}
              />
            }
            label="Deactivated"
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Order"
            type="number"
            slotProps={{ htmlInput: { min: 1, max: orderMax, step: 1 } }}
            value={dashboard.orderBy}
            onChange={(e) => handleChange('orderBy', parseInt(e.target.value) || 1)}
            helperText={`Min 1, Max ${orderMax}`}
          />
        </Grid>

        {/* New Field: Icon */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Icon"
            value={dashboard.icon || ''}
            onChange={(e) => handleChange('icon', e.target.value)}
            placeholder="Enter icon name or URL"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <InsertEmoticonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        {/* New Field: Thumbnail */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Thumbnail"
            value={dashboard.thumbnail || ''}
            onChange={(e) => handleChange('thumbnail', e.target.value)}
            placeholder="Enter thumbnail image URL"
            helperText="CDN URL to the image for the Hub screen."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ImageIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        {/* New Field: Demo */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Demo"
            value={dashboard.demo || ''}
            onChange={(e) => handleChange('demo', e.target.value)}
            placeholder="Enter demo URL or information"
            helperText="CDN URL to the image for the No Access/FOMO screen."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <OndemandVideoIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Resource"
            value={dashboard.resource}
            onChange={(e) => handleChange('resource', e.target.value)}
            placeholder="Enter resource"
            helperText="Superset URL Slug for the dashboard, used in SE."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Embed Id"
            value={dashboard.embedId || ''}
            onChange={(e) => handleChange('embedId', e.target.value)}
            placeholder="Enter embed id"
            helperText="Superset Embedded Id for the dashboard, used in PLC and Listen-Web."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CodeIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        
        {/* New Field: Downtime with DateTimePicker */}
        <Grid size={{ xs: 12 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Scheduled Downtime"
              value={dashboard.downtime ? new Date(dashboard.downtime) : null}
              onChange={(date) => handleChange('downtime', date ? date.toISOString() : null)}
              slotProps={{
                textField: { 
                  fullWidth: true,
                  variant: 'outlined',
                  InputProps: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <TimerIcon color="action" />
                      </InputAdornment>
                    )
                  }
                }
              }}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
      
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <LoadingButton 
          type="submit" 
          variant="contained" 
          loading={isSubmitting}
        >
          {isNew ? 'Create' : 'Save'}
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default DashboardForm;